// @ts-nocheck
import { BigNumber } from '@ethersproject/bignumber'
import type { TransactionResponse } from '@ethersproject/providers'
import { Currency, CurrencyAmount, Percent } from '@uniswap/sdk-core'
import { FeeAmount } from '@uniswap/v3-sdk'
import useActiveWeb3React from "hooks/useActiveWeb3React";
import UnsupportedCurrencyFooter from 'components/swap/UnsupportedCurrencyFooter'
import useClubManager from "hooks/useClubManager";
import { useCallback, useContext, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import CurrencyInputPanelAsset from "../../components/CurrencyInputPanel/asset";
import CurrencyInputPanelLimited from "../../components/CurrencyInputPanel/limited";
import { DEFAULT_TXN_DISMISS_MS } from "../../constants/misc";
import {
  useRangeHopCallbacks,
  useV3DerivedMintInfo,
  useV3MintActionHandlers,
  useV3MintState,
} from 'state/mint/v3/hooks'
import {
  useAddPopup,
  useWalletModalToggle
} from "../../state/application/hooks";
import { AutoColumn } from '../../components/Column'
import TransactionConfirmationModal, { ConfirmationModalContent } from '../../components/TransactionConfirmationModal'
import { WRAPPED_NATIVE_CURRENCY } from '../../constants/tokens'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallback } from '../../hooks/useApproveCallback'
import { useArgentWalletContract } from '../../hooks/useArgentWalletContract'
import { useDerivedPositionInfo } from '../../hooks/useDerivedPositionInfo'
import { useIsSwapUnsupported } from '../../hooks/useIsSwapUnsupported'
import { useUSDCValue } from "../../hooks/useUSDCPrice";
import useTransactionDeadline from '../../hooks/useTransactionDeadline'
import { useV3PositionFromTokenId } from '../../hooks/useV3Positions'
import useVaultManager from "hooks/useVaultManager";
import { Bound, Field } from '../../state/mint/v3/actions'
import { useTransactionAdder } from '../../state/transactions/hooks'
import { useVaultManagerContract } from "../../hooks/useContract";
import { TransactionType } from '../../state/transactions/types'
import { ExternalLink, ThemedText } from '../../theme'
import { useCurrencyBalance } from "../../state/wallet/hooks";
import { currencyId } from '../../utils/currencyId'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import {
  DynamicSection,
  HideMedium,
  MediumOnly,
  PageWrapper,
  RightContainer,
  ScrollablePage,
  StackedContainer,
  StackedItem,
  Wrapper
} from "./styled";
import {
  AMM_VAULT_ADDRESSES,
  ASSET_VAULT_ADDRESSES
} from "../../constants/addresses";
import { AddRemoveTabs } from 'components/NavigationTabs';
import Row, { AutoRow, RowBetween, RowFixed } from 'components/Row';
import { ButtonError, ButtonPrimary, ButtonText } from 'components/Button';
import RateToggle from 'components/RateToggle';
import FeeSelector from 'components/FeeSelector';
import HoverInlineText from 'components/HoverInlineText';
import { OutlineCard } from 'components/Card';
import RangeSelector from 'components/RangeSelector';
import { AlertTriangle } from 'react-feather';
import { PositionPreview } from 'components/PositionPreview';
import LiquidityChartRangeInput from 'components/LiquidityChartRangeInput';
import PresetsButtons from 'components/RangeSelector/PresetsButtons';
import { ThemeContext } from 'styled-components/macro';
import { Review } from './Review';

const DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE = new Percent(50, 10_000)

export default function AddLiquidityV3() {
  const history = useHistory()
  const theme = useContext(ThemeContext)
  const {
    currencyIdA,
    currencyIdB,
    feeAmount: feeAmountFromUrl,
    tokenId,
  } = useParams<{ currencyIdA?: string; currencyIdB?: string; feeAmount?: string; tokenId?: string }>()
  const { account, chainId, library } = useActiveWeb3React()

  const [waiting, setWaiting] = useState<boolean>(false)
  const [invalidA, setInvalidA] = useState<boolean>(false)
  const [invalidB, setInvalidB] = useState<boolean>(false)
  const [showCapitalEfficiencyWarning, setShowCapitalEfficiencyWarning] = useState(false)

  const addTransaction = useTransactionAdder()
  const { supplyAllowance } = useClubManager();
  const poolType = window.localStorage.getItem('SELECT_POOL') || 'UniswapV2'
  const vaultManager = useVaultManagerContract(poolType.toLowerCase(), true)
  const addPopup = useAddPopup()
  // const positionManager = useV3NFTPositionManagerContract()
  // const parsedQs = useParsedQueryString()
  const showConnectAWallet = Boolean(!account)
  const toggleWalletModal = useWalletModalToggle()
  // check for existing position if tokenId in url
  const { position: existingPositionDetails, loading: positionLoading } = useV3PositionFromTokenId(
    tokenId ? BigNumber.from(tokenId) : undefined
  )
  const hasExistingPosition = !!existingPositionDetails && !positionLoading
  const { position: existingPosition } = useDerivedPositionInfo(existingPositionDetails)

  // fee selection from url
  const feeAmount: FeeAmount | undefined =
    feeAmountFromUrl && Object.values(FeeAmount).includes(parseFloat(feeAmountFromUrl))
      ? parseFloat(feeAmountFromUrl)
      : undefined
  // const feeAmount = FeeAmount.MEDIUM;
  const baseCurrency = useCurrency(currencyIdA)
  const currencyB = useCurrency(currencyIdB)
  // prevent an error if they input ETH/WETH
  const quoteCurrency =
    baseCurrency && currencyB && baseCurrency.wrapped.equals(currencyB.wrapped) ? undefined : currencyB

  // mint state
  const { independentField, typedValue, startPriceTypedValue } =
    useV3MintState()

  const {
    pool,
    ticks,
    dependentField,
    price,
    pricesAtTicks,
    parsedAmounts,
    currencyBalances,
    position,
    noLiquidity,
    currencies,
    errorMessage,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    ticksAtLimit
  } = useV3DerivedMintInfo(
    baseCurrency ?? undefined,
    quoteCurrency ?? undefined,
    feeAmount,
    baseCurrency ?? undefined,
    existingPosition
  )

  const { onFieldAInput, onFieldBInput, onLeftRangeInput, onRightRangeInput } =
    useV3MintActionHandlers(noLiquidity)

  const isValid = !errorMessage && !invalidRange;
  // modal and loading
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm

  // txn values
  const deadline = useTransactionDeadline() // custom from users settings

  const [txHash, setTxHash] = useState<string>('')

  // get formatted amounts
  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: parsedAmounts[dependentField]?.toSignificant(6) ?? '',
  }

  const usdcValues = {
    [Field.CURRENCY_A]: useUSDCValue(parsedAmounts[Field.CURRENCY_A]),
    [Field.CURRENCY_B]: useUSDCValue(parsedAmounts[Field.CURRENCY_B]),
  }

  // get the max amounts user can add
  const maxAmounts: { [field in Field]?: CurrencyAmount<Currency> } = [Field.CURRENCY_A, Field.CURRENCY_B].reduce(
    (accumulator, field) => {
      return {
        ...accumulator,
        [field]: maxAmountSpend(currencyBalances[field]),
      }
    },
    {}
  )

  const assetMax = useCurrencyBalance(ASSET_VAULT_ADDRESSES[chainId!], currencies[Field.CURRENCY_B] ?? undefined);

  const argentWalletContract = useArgentWalletContract()

  // check whether the user has approved the router on the tokens
  const [approval, approveCallback] = useApproveCallback(
    argentWalletContract ? undefined : parsedAmounts[Field.CURRENCY_A],
    chainId ? AMM_VAULT_ADDRESSES[chainId][poolType.toLowerCase()] : undefined
  )
  async function onAdd() {
    if (!chainId || !library || !account) return

    if (!baseCurrency || !quoteCurrency) {
      return
    }
    console.log(tokenId);
    if (vaultManager && account && deadline) {
      try {
        setWaiting(true);
        console.log((parsedAmounts[Field.CURRENCY_A])?.quotient.toString());
        await vaultManager.addLiquidity(currencyId(baseCurrency), (parsedAmounts[Field.CURRENCY_A])?.quotient.toString(), currencyId(quoteCurrency), feeAmount, tickLower, tickUpper)
          .then(async (response: TransactionResponse) => {
            setTxHash(response.hash)
            addTransaction(response, {
              type: TransactionType.ADD_LIQUIDITY_V3_POOL,
              baseCurrencyId: currencyId(baseCurrency),
              quoteCurrencyId: currencyId(quoteCurrency),
              createPool: Boolean(noLiquidity),
              expectedAmountBaseRaw: parsedAmounts[Field.CURRENCY_A]?.quotient?.toString() ?? '0',
              expectedAmountQuoteRaw: parsedAmounts[Field.CURRENCY_B]?.quotient?.toString() ?? '0',
              feeAmount,
            })
            setShowConfirm(true)
            await response.wait();
            setAttemptingTxn(false)
            setWaiting(false);
          })

        ReactGA.event({
          category: 'Liquidity',
          action: 'Add',
          label: [currencies[Field.CURRENCY_A]?.symbol, currencies[Field.CURRENCY_B]?.symbol].join('/'),
        })
      }
      catch (error) {
        setWaiting(false);
        console.error('Failed to send transaction', error)
        if(error?.error) {
          addPopup(
            {
              reason: error?.error.message
            },
            Date.now(),
            DEFAULT_TXN_DISMISS_MS
          )
        }
        setAttemptingTxn(false)
        // we only care if the error is something _other_ than the user rejected the tx
        if (error?.code !== 4001) {
          console.error(error)
        }
      }
    }
  }

  const onUserInputA = (e) => {
    const decimal = currencies[Field.CURRENCY_A]?.decimals || 18;
    const removed = removeUnneededCharacter(e.toString())
    const splited = removed.toString().split('.')
    if (splited.length > 1) {
      setInvalidA(splited[1].length > decimal)
    } else {
      setInvalidA(false)
    }
    onFieldAInput(removed.toString())
  }

  const onUserInputB = (e) => {
    const decimal = currencies[Field.CURRENCY_B]?.decimals || 18;
    const removed = removeUnneededCharacter(e.toString())
    const splited = removed.toString().split('.')
    if (splited.length > 1) {
      setInvalidB(splited[1].length > decimal)
    } else {
      setInvalidB(false)
    }
    onFieldBInput(removed.toString())
  }

  const removeUnneededCharacter = (str: string) => {
    if (str.startsWith('0.')) {
      return str
    } else if (str.startsWith('00')) {
      return str.substring(2)
    } else if (str.length > 1 && str.startsWith('0')) {
      return str.substring(1)
    }
    return str
  }

  const handleFeePoolSelect = useCallback(
    (newFeeAmount: FeeAmount) => {
      onLeftRangeInput('')
      onRightRangeInput('')
      history.push(`/add/${currencyIdA}/${currencyIdB}/${newFeeAmount}`)
    },
    [currencyIdA, currencyIdB, history, onLeftRangeInput, onRightRangeInput]
  )

  const handleCurrencySelect = useCallback(
    (currencyNew: Currency, currencyIdOther?: string): (string | undefined)[] => {
      const currencyIdNew = currencyId(currencyNew)

      if (currencyIdNew === currencyIdOther) {
        // not ideal, but for now clobber the other if the currency ids are equal
        return [currencyIdNew, undefined]
      } else {
        // prevent weth + eth
        const isETHOrWETHNew =
          currencyIdNew === 'ETH' ||
          (chainId !== undefined && currencyIdNew === WRAPPED_NATIVE_CURRENCY[chainId]?.address)
        const isETHOrWETHOther =
          currencyIdOther !== undefined &&
          (currencyIdOther === 'ETH' ||
            (chainId !== undefined && currencyIdOther === WRAPPED_NATIVE_CURRENCY[chainId]?.address))

        if (isETHOrWETHNew && isETHOrWETHOther) {
          return [currencyIdNew, undefined]
        } else {
          return [currencyIdNew, currencyIdOther]
        }
      }
    },
    [chainId]
  )

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onFieldAInput('')
      // dont jump to pool page if creating
      history.push('/pool')
    }
    setTxHash('')
  }, [history, onFieldAInput, txHash])

  const addIsUnsupported = useIsSwapUnsupported(currencies?.CURRENCY_A, currencies?.CURRENCY_B)
  const clearAll = useCallback(() => {
    onFieldAInput('')
    onFieldBInput('')
    onLeftRangeInput('')
    onRightRangeInput('')
    history.push(`/add`)
  }, [history, onFieldAInput, onFieldBInput, onLeftRangeInput, onRightRangeInput])
  // get value and prices at ticks
  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks
  const { [Bound.LOWER]: priceLower, [Bound.UPPER]: priceUpper } = pricesAtTicks
  
  const { getDecrementLower, getIncrementLower, getDecrementUpper, getIncrementUpper, getSetFullRange } =
    useRangeHopCallbacks(baseCurrency ?? undefined, quoteCurrency ?? undefined, feeAmount, tickLower, tickUpper, pool)
  const { capitalInUSD } = useVaultManager(currencies[Field.CURRENCY_A]?.address, (parsedAmounts[Field.CURRENCY_A])?.quotient.toString() ?? "0");
  const pendingText = `Supplying ${!depositADisabled ? parsedAmounts[Field.CURRENCY_A]?.toSignificant(6) : ''} ${!depositADisabled ? currencies[Field.CURRENCY_A]?.symbol : ''
    } ${!outOfRange ? 'and' : ''} ${!depositBDisabled ? parsedAmounts[Field.CURRENCY_B]?.toSignificant(6) : ''} ${!depositBDisabled ? currencies[Field.CURRENCY_B]?.symbol : ''
    }`

    const handleCurrencyASelect = useCallback(
      (currencyANew: Currency) => {
        const [idA, idB] = handleCurrencySelect(currencyANew, currencyIdB)
        if (idB === undefined) {
          history.push(`/add/${idA}`)
        } else {
          history.push(`/add/${idA}/${idB}`)
        }
      },
      [handleCurrencySelect, currencyIdB, history]
    )
  
    const handleCurrencyBSelect = useCallback(
      (currencyBNew: Currency) => {
        const [idB, idA] = handleCurrencySelect(currencyBNew, currencyIdA)
        if (idA === undefined) {
          history.push(`/add/${idB}`)
        } else {
          history.push(`/add/${idA}/${idB}`)
        }
      },
      [handleCurrencySelect, currencyIdA, history]
    )
  const Buttons = () =>
    addIsUnsupported ? (
      <ButtonPrimary disabled={true} $borderRadius="12px" padding={'12px'}>
        <ThemedText.Main mb="4px">
          Unsupported Asset
        </ThemedText.Main>
      </ButtonPrimary>
    ) : showConnectAWallet ? (
      <div className='add-liquidity-warrap'>
        <button className='add-liquidity' style={{ border: "0px" }} onClick={toggleWalletModal}><p>Connect Wallet</p></button>
      </div>
    ) : (
      <AutoColumn gap={'md'}>
        {(
          !isValid || noLiquidity
            ?
              <div className='add-liquidity-warrap' style={{opacity:'0.45'}}>
                <button className='add-liquidity' style={{border:'0px',cursor: 'not-allowed'}} >
                  <p>{noLiquidity || invalidPool ? "Invalid Pair" : errorMessage }</p>
                </button>
              </div>
            :
            (
              (invalidA || invalidB)
                ?
                <div className='add-liquidity-footer'>
                  <button className={(invalidA || invalidB) ? 'primary-button-invalid': 'primary-button'} disabled={true}>
                    <p>{(invalidA || invalidB) ? 'Invalid Amount' : 'Insufficient Balance'}</p>
                  </button>
                </div>
                :
                <div className='add-liquidity-footer'>
                  {
                    approval === ApprovalState.NOT_APPROVED
                      ?
                      <button className='Approve-pair' style={{ border: "0px" }} onClick={approveCallback} >Approve</button>
                      :
                      (
                        approval === ApprovalState.PENDING
                          ?
                          <button className='primary-button' disabled={true}>
                            <p style={{ color: "white" }}>Approving...</p>
                          </button>
                          :
                          (
                            supplyAllowance.gt(capitalInUSD) ?
                            <ButtonError
                              onClick={() => {
                                setShowConfirm(true)
                              }}
                              disabled={
                                !isValid ||
                                (!argentWalletContract && approval !== ApprovalState.APPROVED && !depositADisabled)
                              }
                              error={!isValid && !!parsedAmounts[Field.CURRENCY_A] && !!parsedAmounts[Field.CURRENCY_B]}
                            >
                              <>{errorMessage ? errorMessage : <>Preview</>}</>
                            </ButtonError>
                            :
                            <div className='add-liquidity-warrap' style={waiting ? { opacity: 0.5 }: {}}>
                              <button className='primary-button-invalid' disabled={true}>
                              <p>{'Check your allowance from DDC tab'}</p>
                              </button>
                            </div>
                          )
                      )
                  }
                </div>
            )
        )}
      </AutoColumn>
    )
  return (
    <>
      <ScrollablePage>
        <TransactionConfirmationModal
          isOpen={showConfirm}
          onDismiss={handleDismissConfirmation}
          attemptingTxn={attemptingTxn}
          hash={txHash}
          content={() => (
              <ConfirmationModalContent
                title={<>Add Liquidity</>}
                onDismiss={handleDismissConfirmation}
                preview={true}
                topContent={() => (
                  <Review
                    parsedAmounts={parsedAmounts}
                    position={position}
                    existingPosition={existingPosition}
                    priceLower={priceLower}
                    priceUpper={priceUpper}
                    outOfRange={outOfRange}
                    ticksAtLimit={ticksAtLimit}
                  />
                )}
                bottomContent={() => (
                  <div className='add-liquidity-warrap' style={waiting ? { opacity: 0.5 }: {}}>
                      <button className='add-liquidity-modal-button' style={!waiting?{ border: "0px" }:{border:'0px',cursor: 'not-allowed',opacity:'0.45'}} onClick={onAdd}>
                        <p>{waiting?'Transaction in progress-Please wait':'Add Liquidity'}</p>
                      </button>
                    </div>
                )}
              />
          )}
          pendingText={pendingText}
        />
        <div className='add-liquidity-page'>
          <div className='remove-tab-warrap'>
            <PageWrapper wide={!hasExistingPosition}>
              {/* <HeaderTabs
                creating={false}
                adding={true}
                migrate={false}
                positionID={tokenId}
                defaultSlippage={DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE}
                showBackLink={!hasExistingPosition}
              >
              </HeaderTabs>
              <Wrapper className='remove-tab-content' style={{marginBottom: 0}}>
                <ResponsiveTwoColumns wide={!hasExistingPosition} style={{ display: "block" }} className="remove-tab-content-inner">
                  <div>
                    
                  </div>
                </ResponsiveTwoColumns>
              </Wrapper>
               */}
              <AddRemoveTabs
                creating={false}
                adding={true}
                positionID={tokenId}
                defaultSlippage={DEFAULT_ADD_IN_RANGE_SLIPPAGE_TOLERANCE}
                showBackLink={!hasExistingPosition}
              >
                {!hasExistingPosition && (
                  <Row justifyContent="flex-end" style={{ width: 'fit-content', minWidth: 'fit-content' }}>
                    <MediumOnly>
                      <ButtonText onClick={clearAll} margin="0 15px 0 0">
                        <ThemedText.Blue fontSize="12px">
                          <>Clear All</>
                        </ThemedText.Blue>
                      </ButtonText>
                    </MediumOnly>
                    {baseCurrency && quoteCurrency ? (
                      <RateToggle
                        currencyA={baseCurrency}
                        currencyB={quoteCurrency}
                        handleRateToggle={() => {
                          if (!ticksAtLimit[Bound.LOWER] && !ticksAtLimit[Bound.UPPER]) {
                            onLeftRangeInput((invertPrice ? priceLower : priceUpper?.invert())?.toSignificant(6) ?? '')
                            onRightRangeInput((invertPrice ? priceUpper : priceLower?.invert())?.toSignificant(6) ?? '')
                            onFieldAInput(formattedAmounts[Field.CURRENCY_B] ?? '')
                          }
                          history.push(
                            `/add/${currencyIdB as string}/${currencyIdA as string}${feeAmount ? '/' + feeAmount : ''}`
                          )
                        }}
                      />
                    ) : null}
                  </Row>
                )}
              </AddRemoveTabs>
              <Wrapper>
                  <AutoColumn gap="lg">
                    {hasExistingPosition && existingPosition && (
                      <PositionPreview
                        position={existingPosition}
                        title={<>Selected Range</>}
                        inRange={!outOfRange}
                        ticksAtLimit={ticksAtLimit}
                      />
                    )}
                  </AutoColumn>
                  <div>
                    <DynamicSection
                      disabled={tickLower === undefined || tickUpper === undefined || invalidPool || invalidRange}
                    >
                      <AutoColumn gap="md">
                        <ThemedText.Label style={{ fontSize: "16px", color: "white", fontWeight:'400px',marginBottom:'5px',height:'24px' }}>
                          Capital
                        </ThemedText.Label>
                        <div className='fromToken' style={{ display: "flex", justifyContent: "space-between" }}>
                          <CurrencyInputPanelLimited
                            value={formattedAmounts[Field.CURRENCY_A]}
                            onUserInput={onUserInputA}
                            onMax={() => {
                              onFieldAInput(maxAmounts[Field.CURRENCY_A]?.toExact(6) ?? '')
                            }}
                            showMaxButton={false}
                            onCurrencySelect={handleCurrencyASelect}
                            currency={currencies[Field.CURRENCY_A] ?? null}
                            id="add-liquidity-input-tokena"
                            fiatValue={usdcValues[Field.CURRENCY_A]}
                            disabled={waiting || approval === ApprovalState.PENDING}
                            showCommonBases
                            locked={depositADisabled}
                          />
                        </div>
                        <ThemedText.Label  style={{ fontSize: "16px", color: "white", fontWeight:'400px',marginBottom:'5px',height:'24px' }}>
                          Token
                        </ThemedText.Label>
                        <div className='toToken' style={{ display: "flex", justifyContent: "space-between" }}>
                          <CurrencyInputPanelAsset
                            value={formattedAmounts[Field.CURRENCY_B]}
                            onUserInput={onUserInputB}
                            onMax={() => {
                              onFieldBInput(assetMax?.toExact(6) ?? '')
                            }}
                            showMaxButton={false}
                            fiatValue={usdcValues[Field.CURRENCY_B]}
                            currency={currencies[Field.CURRENCY_B] ?? null}
                            onCurrencySelect={handleCurrencyBSelect}
                            id="add-liquidity-input-tokenb"
                            disabled={waiting || approval === ApprovalState.PENDING}
                            showCommonBases
                          />
                        </div>
                      </AutoColumn>
                    </DynamicSection>
                  </div>

                  {!hasExistingPosition ? (
                    <>
                      <RightContainer gap="lg">
                        <DynamicSection>
                          <FeeSelector
                            disabled={!quoteCurrency || !baseCurrency}
                            feeAmount={feeAmount}
                            handleFeePoolSelect={handleFeePoolSelect}
                            currencyA={baseCurrency ?? undefined}
                            currencyB={quoteCurrency ?? undefined}
                          />
                        </DynamicSection>
                        <DynamicSection gap="md" disabled={!feeAmount || invalidPool}>
                          {!noLiquidity && (
                            <>
                              <RowBetween>
                                <ThemedText.Label>
                                  <>Set Price Range</>
                                </ThemedText.Label>
                              </RowBetween>

                              {price && baseCurrency && quoteCurrency && !noLiquidity && (
                                <AutoRow gap="4px" justify="center" style={{ marginTop: '0.5rem' }}>
                                  <>
                                    <ThemedText.Main fontWeight={500} textAlign="center" fontSize={12} color="text1">
                                      Current Price:
                                    </ThemedText.Main>
                                    <ThemedText.Body fontWeight={500} textAlign="center" fontSize={12} color="text1">
                                      <HoverInlineText
                                        maxCharacters={20}
                                        text={invertPrice ? price.invert().toSignificant(6) : price.toSignificant(6)}
                                      />
                                    </ThemedText.Body>
                                    <ThemedText.Body color="text2" fontSize={12}>
                                      {quoteCurrency?.symbol} per {baseCurrency.symbol}
                                    </ThemedText.Body>
                                  </>
                                </AutoRow>
                              )}

                              <LiquidityChartRangeInput
                                currencyA={baseCurrency ?? undefined}
                                currencyB={quoteCurrency ?? undefined}
                                feeAmount={feeAmount}
                                ticksAtLimit={ticksAtLimit}
                                price={
                                  price ? parseFloat((invertPrice ? price.invert() : price).toSignificant(8)) : undefined
                                }
                                priceLower={priceLower}
                                priceUpper={priceUpper}
                                onLeftRangeInput={onLeftRangeInput}
                                onRightRangeInput={onRightRangeInput}
                                interactive={!hasExistingPosition}
                              />
                            </>
                          )}
                        </DynamicSection>

                        <DynamicSection
                          gap="md"
                          disabled={!feeAmount || invalidPool || (noLiquidity && !startPriceTypedValue)}
                        >
                          <StackedContainer>
                            <StackedItem style={{ opacity: showCapitalEfficiencyWarning ? '0.05' : 1 }}>
                              <AutoColumn gap="md">
                                {/* {noLiquidity && (
                                  <RowBetween>
                                    <ThemedText.Label>
                                      <>Set Price Range</>
                                    </ThemedText.Label>
                                  </RowBetween>
                                )} */}
                                {!noLiquidity && (
                                  <>
                                    <RangeSelector
                                      priceLower={priceLower}
                                      priceUpper={priceUpper}
                                      getDecrementLower={getDecrementLower}
                                      getIncrementLower={getIncrementLower}
                                      getDecrementUpper={getDecrementUpper}
                                      getIncrementUpper={getIncrementUpper}
                                      onLeftRangeInput={onLeftRangeInput}
                                      onRightRangeInput={onRightRangeInput}
                                      currencyA={baseCurrency}
                                      currencyB={quoteCurrency}
                                      feeAmount={feeAmount}
                                      ticksAtLimit={ticksAtLimit}
                                    />
                                    <PresetsButtons
                                      setFullRange={() => {
                                        setShowCapitalEfficiencyWarning(true)
                                      }}
                                    />
                                  </>
                                )}
                              </AutoColumn>
                            </StackedItem>

                            {showCapitalEfficiencyWarning && (
                              <StackedItem zIndex={1}>
                                <OutlineCard
                                  padding="15px"
                                  $borderRadius="12px"
                                  height="100%"
                                  style={{
                                    borderColor: theme.yellow3,
                                    border: '1px solid',
                                  }}
                                >
                                  <AutoColumn gap="8px" style={{ height: '100%' }}>
                                    <RowFixed>
                                      <AlertTriangle stroke={theme.yellow3} size="16px" />
                                      <ThemedText.White ml="12px" fontSize="15px">
                                        <>Efficiency Comparison</>
                                      </ThemedText.White>
                                    </RowFixed>
                                    <RowFixed>
                                      <ThemedText.White ml="12px" fontSize="13px" margin={0} fontWeight={400}>
                                        <>
                                          Full range positions may earn less fees than concentrated positions. Learn more{' '}
                                          <ExternalLink
                                            style={{ color: theme.yellow3, textDecoration: 'underline' }}
                                            href={
                                              'https://help.uniswap.org/en/articles/5434296-can-i-provide-liquidity-over-the-full-range-in-v3'
                                            }
                                          >
                                            here
                                          </ExternalLink>
                                          .
                                        </>
                                      </ThemedText.White>
                                    </RowFixed>
                                    <Row padding={"1rem"}>
                                      <button
                                        className='primary-button'
                                        onClick={() => {
                                          setShowCapitalEfficiencyWarning(false)
                                          getSetFullRange()
                                        }}
                                      >
                                        <ThemedText.White fontSize={13} color="black">
                                          <>I understand</>
                                        </ThemedText.White>
                                      </button>
                                    </Row>
                                  </AutoColumn>
                                </OutlineCard>
                              </StackedItem>
                            )}
                          </StackedContainer>

                          {outOfRange ? (
                            <OutlineCard padding="8px 12px" $borderRadius="12px">
                              <RowBetween>
                                <AlertTriangle stroke={theme.yellow3} size="16px" />
                                <ThemedText.White ml="12px" fontSize="12px">
                                  <>
                                    Your position will not earn fees or be used in trades until the market price moves into
                                    your range.
                                  </>
                                </ThemedText.White>
                              </RowBetween>
                            </OutlineCard>
                          ) : null}

                          {invalidRange ? (
                            <OutlineCard padding="8px 12px" $borderRadius="12px">
                              <RowBetween>
                                <AlertTriangle stroke={theme.yellow3} size="16px" />
                                <ThemedText.White ml="12px" fontSize="12px">
                                  <>Invalid range selected. The min price must be lower than the max price.</>
                                </ThemedText.White>
                              </RowBetween>
                            </OutlineCard>
                          ) : null}
                        </DynamicSection>

                        <MediumOnly>
                          <Buttons />
                        </MediumOnly>
                      </RightContainer>
                      <HideMedium>
                        <Buttons />
                      </HideMedium>
                    </>
                  ) : (
                    <Buttons />
                  )}
              </Wrapper>
            </PageWrapper>
          </div>
        </div>
        {addIsUnsupported && (
          <UnsupportedCurrencyFooter
            show={addIsUnsupported}
            currencies={[currencies.CURRENCY_A, currencies.CURRENCY_B]}
          />
        )}
      </ScrollablePage>
    </>
  )
}
